import { useState, useEffect } from "react"

/**
 * useDebounce - A reusable debounce hook.
 *
 * @param value  - The value to debounce
 * @param delay  - Delay in milliseconds (default: 400ms)
 * @returns        The debounced value, updated only after the delay has passed
 *                 without a new value being set.
 *
 * @example
 * const debouncedSearch = useDebounce(searchTerm, 400)
 * useEffect(() => {
 *   // API call here, only fires 400ms after the user stops typing
 * }, [debouncedSearch])
 */
const useDebounce = <T>(value: T, delay: number = 400): T => {
    const [debouncedValue, setDebouncedValue] = useState<T>(value)

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value)
        }, delay)

        return () => {
            clearTimeout(timer)
        }
    }, [value, delay])

    return debouncedValue
}

export default useDebounce
